// eslint-disable-next-line import/no-cycle
import { contentIsChunked } from '../contentFramePortalGateInternalMethods/chunkedContent';
import type { RangyRange } from '../types/rangy';
import { extractStartAndEndIfValid } from './locationSerialization/base';
import { findChunkContainerForRange } from './locationSerialization/chunked';
import Renderer from './Renderer';

interface DomTraversalResult {
  allTextNodes: Node[];
}

/**
 * Computes the offset of a highlight within the document.
 *
 * This function also works correctly with chunked docs by counting the number of characters
 * before the chunk that the highlight is in, as well as the number of characters within the chunk.
 */
export default function computeHighlightOffset(
  expandedRange: RangyRange,
  renderer: Renderer,
): number {
  const fallbackOffset = expandedRange.getBookmark().start;

  if (!contentIsChunked()) {
    return fallbackOffset;
  }

  const chunkElement = findChunkContainerForRange(expandedRange, renderer.containerNode);
  if (!chunkElement) {
    return fallbackOffset;
  }

  let startContainer: Node;
  let startOffset: number;
  try {
    const extracted = extractStartAndEndIfValid(expandedRange, renderer.containerNode);
    startContainer = extracted.startContainer;
    startOffset = extracted.startOffset;
  } catch {
    return fallbackOffset;
  }

  const chunkIndex = parseInt(chunkElement.dataset.chunkIndex, 10);
  const precedingTextCharacterCount = countPrecedingTextCharacters(chunkIndex, renderer.containerNode);
  const intraChunkCharacterCount = countIntraChunkOffset(chunkElement, startContainer, startOffset);

  return precedingTextCharacterCount + intraChunkCharacterCount;
}

function countPrecedingTextCharacters(
  targetChunkIndex: number,
  rootElement: Document | Element,
): number {
  const chunkContainers = Array.from(rootElement.querySelectorAll('.rw-chunk-container'))
    .slice(0, targetChunkIndex);
  return chunkContainers.reduce((sum, container) => {
    const textCount = parseInt(container.getAttribute('data-text-character-count') || '0', 10);
    return sum + textCount;
  }, 0);
}

function countIntraChunkOffset(
  chunkElement: Element,
  startContainer: Node,
  startOffset: number,
): number {
  const { allTextNodes } = traverseAndCollectNodesFromDOM(chunkElement);

  if (startContainer.nodeType === Node.TEXT_NODE) {
    return getBackwardTextLength(allTextNodes, startContainer) + startOffset;
  }

  // startContainer is an element; startOffset is a child index. The boundary is BEFORE
  // child[startOffset], or AFTER startContainer's last child when startOffset equals
  // childNodes.length (e.g. setStartAfter(lastChild)). Find the first text node at or
  // after that DOM position within the chunk — falling back to allTextNodes[0] would
  // always yield offset = chunk start, which is wrong for any selection past the first
  // paragraph (e.g. an "h" keystroke that selects an entire <p> element).
  const childAtStartOffset = startContainer.childNodes[startOffset];
  const isAfterLastChild = childAtStartOffset === undefined;
  const boundaryNode = childAtStartOffset ?? startContainer;

  const targetNode = allTextNodes.find((textNode) => {
    if (textNode === boundaryNode) {
      return true;
    }
    const pos = boundaryNode.compareDocumentPosition(textNode);
    if (isAfterLastChild) {
      // Boundary is past startContainer's content. Skip text nodes inside startContainer.
      return Boolean(pos & Node.DOCUMENT_POSITION_FOLLOWING) &&
        !(pos & Node.DOCUMENT_POSITION_CONTAINED_BY);
    }
    return Boolean(pos & Node.DOCUMENT_POSITION_CONTAINED_BY) ||
      Boolean(pos & Node.DOCUMENT_POSITION_FOLLOWING);
  });

  if (targetNode) {
    return getBackwardTextLength(allTextNodes, targetNode);
  }

  // No text node at or after the boundary in this chunk — boundary sits at chunk end.
  return allTextNodes.reduce((sum, node) => sum + (node.textContent?.length ?? 0), 0);
}

function traverseAndCollectNodesFromDOM(chunkElement: Element): DomTraversalResult {
  const allTextNodes: Node[] = [];
  const walker = chunkElement.ownerDocument!.createTreeWalker(
    chunkElement,
    NodeFilter.SHOW_TEXT,
  );

  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      allTextNodes.push(node);
    }
    node = walker.nextNode();
  }

  return { allTextNodes };
}

function getBackwardTextLength(allTextNodes: Node[], targetNode: Node): number {
  let length = 0;

  for (const node of allTextNodes) {
    if (node === targetNode) {
      return length;
    }

    const textContent = node.textContent || '';
    length += textContent.length;
  }

  return 0;
}
